import {IconButton} from '@dropbox/dig-components/buttons';
import {Split, Stack} from '@dropbox/dig-foundations';
import {UIIcon} from '@dropbox/dig-icons';
import {AddLine} from '@dropbox/dig-icons/assets';
import {useQuery} from '@tanstack/react-query';
import {pulseUserAtom} from 'atoms/auth';
import {NotificationService} from 'client';
import {Layout} from 'components/DSYS/Layout';
import {Table} from 'components/DSYS/Table';
import {Title} from 'components/DSYS/Title';
import {ROUTE_PATHS} from 'constant';
import {isSuperUser, isUserAdmin} from 'helpers/utils';
import {useDocumentTitle} from 'hooks/useDocumentTitle';
import {useAtomValue} from 'jotai';
import {useState} from 'react';
import {useNavigate} from 'react-router';
import {getService} from 'utilities';

import {NewNotificationModal} from './notifications/NewNotificationModal';
import {NotificationRow} from './notifications/NotificationRow';
import {ReadinessAttentionNotificationModal} from './notifications/ReadinessAttentionNotificationModal';
import {SendNowMenu, SendNowNotificationType} from './notifications/SendNowMenu';
import {fromServerDate, getTodayUTC9am} from './utils';

const columns = [
  {type: 'type', minWidth: 100},
  {type: 'data', minWidth: 200},
  {type: 'audience', minWidth: 200},
  {type: 'send', minWidth: 200},
  {type: 'due', minWidth: 200},
  {type: 'delete', minWidth: 100},
];

export const NotificationScheduler = () => {
  const {data} = useQuery({
    queryKey: ['notifications', 'scheduled'],
    queryFn: () => getService(NotificationService).scheduledApiV1NotificationsScheduledGet(),
  });

  const scheduledData = data?.filter(
    (notification) => fromServerDate(notification.send_at) >= getTodayUTC9am()
  );

  const [notificationModal, setNotificationModal] = useState<
    'scheduled' | SendNowNotificationType | null
  >(null);

  const navigate = useNavigate();

  const pulseUser = useAtomValue(pulseUserAtom);

  useDocumentTitle('Notifications');

  if (!pulseUser) {
    return null;
  }

  if (!isUserAdmin(pulseUser)) {
    navigate('/');
    return null;
  }

  return (
    <Layout.Container
      breadcrumb={[
        {children: 'Admin', to: ROUTE_PATHS.ADMIN},
        {children: 'Notifications', to: ROUTE_PATHS.ADMIN_NOTIFICATIONS},
      ]}
    >
      <Stack gap="24">
        <Title
          size={24}
          withAccessoryEnd={
            <Split gap="8">
              <Split.Item>
                <IconButton
                  aria-label="Add notification"
                  variant="opacity"
                  shape="circular"
                  onClick={() => {
                    setNotificationModal('scheduled');
                  }}
                >
                  <UIIcon src={AddLine} />
                </IconButton>
              </Split.Item>
              <Split.Item>
                <SendNowMenu
                  canSendReadiness={isSuperUser(pulseUser)}
                  onSelect={setNotificationModal}
                />
              </Split.Item>
            </Split>
          }
        >
          Notifications
        </Title>
        <Table columns={columns} data={scheduledData ?? []}>
          <Table.Header />
          <Table.Body>
            {scheduledData?.map((message) => (
              <NotificationRow key={message.id} {...message} />
            ))}
          </Table.Body>
        </Table>
      </Stack>
      {notificationModal === 'scheduled' || notificationModal === 'generic' ? (
        <NewNotificationModal
          sendNow={notificationModal === 'generic'}
          onClose={() => setNotificationModal(null)}
        />
      ) : null}
      {notificationModal === 'readiness_attention' ? (
        <ReadinessAttentionNotificationModal onClose={() => setNotificationModal(null)} />
      ) : null}
    </Layout.Container>
  );
};
